import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { enqueueSnackbar } from 'notistack';
import { type ReactNode, useEffect } from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';
import { checkPermissions } from '@/services/permissions';
import { userAtom } from '@/store/user.store';

import { assetQueryKeys } from '../Assets';
import { selectedOrganizationAtom } from '../Organizations';
import { DEFAULT_SETTINGS } from './Administration.constants';
import type { SettingsTypes } from './Administration.types';

export const administrationActionsAtom = atom<ReactNode>(null);
export const envAtom = atom('');
export const showAlertAtom = atom(false);

type FetchSettingsOptions = {
  showErrorNotification?: boolean;
};

export const useFetchSettings = (
  orgLabel?: string,
  { showErrorNotification = true }: FetchSettingsOptions = {}
) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [user] = useAtom(userAtom);
  const handleError = useHandleError();

  const organization = orgLabel || selectedOrganization?.label || 'all';
  const userHasAdminReadPermission = checkPermissions(
    PERMISSIONS.ADMIN_READ,
    user,
    { ...selectedOrganization, label: organization }
  );

  const { refetch, isError, error, data, isLoading, isSuccess } =
    useQuery<SettingsTypes>({
      queryKey: [`organization_settings/${organization}`],
      enabled: organization !== 'all' && userHasAdminReadPermission, // do not fetch if all organizations are selected
      staleTime: Infinity // do not refetch
    });

  useEffect(() => {
    if (isError && showErrorNotification) {
      handleError(
        error as AxiosError,
        'Failure to fetch organization settings.'
      );
    }
  }, [error, handleError, isError, showErrorNotification]);
  return {
    refetch,
    isLoading,
    isSuccess:
      isSuccess && userHasAdminReadPermission && organization !== 'all',
    isError,
    serverData: data,
    data: data || DEFAULT_SETTINGS
  };
};

/* Mutation to update any item on Admin general or Admin contact page except Response Plans */
export const useMutationSettings = (
  refetch: () => Promise<{ error: unknown; isError: boolean }>,
  organizationLabel?: string
) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  return useMutation({
    mutationFn: (newInfo: Partial<SettingsTypes>) => {
      const updatedInfo = { ...newInfo };
      const label = organizationLabel ?? selectedOrganization.label;

      return axiosClient.put(`/organization_settings/${label}`, updatedInfo);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update organization settings.');
      void refetch();
    },
    onSuccess: async () => {
      const refetchResult = await refetch();
      if (refetchResult.isError) throw refetchResult.error;
      enqueueSnackbar('Organization Information Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organization_settings/${
            organizationLabel ?? selectedOrganization.label
          }`
        ]
      });
      queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
    }
  });
};

/* Mutation to test the Webhook Configuration */
export const useMutationWebhookTest = (eventType: string = 'create') => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  return useMutation({
    mutationFn: () => {
      return axiosClient.post(
        `/organization_settings/${selectedOrganization.label}/test_webhook?event_type=${eventType}`
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`Test Successful`, {
        variant: 'success'
      });
    }
  });
};
